import React, { useState } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { API } from "../config/env";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { loadRazorpayScript } from "../lib/razorpay";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EMPTY_ADDRESS = { full_name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "" };

export default function CheckoutPage() {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { cart, refresh: refreshCart } = useCart();
  const location = useLocation();
  const nav = useNavigate();

  const [address, setAddress] = useState(() => ({
    ...EMPTY_ADDRESS,
    full_name: user?.name || "",
    phone: user?.phone || "",
  }));
  const [paying, setPaying] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const set = (k) => (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));

  if (authLoading) return <div className="pt-40 text-center text-muted-foreground" data-testid="checkout-auth-loading">Loading…</div>;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: "/checkout" }} replace />;

  const requiredFilled = address.full_name && address.phone && address.address_line1 && address.city && address.state && address.pincode;

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data: order } = await axios.post(`${API}/checkout`, { shipping_address: address });
      const { data: payOrder } = await axios.post(`${API}/payment/create-order`, { order_id: order.id });

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Could not load Razorpay checkout. Please try again.");
        setPaying(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: payOrder.key_id,
        amount: payOrder.amount,
        currency: payOrder.currency,
        order_id: payOrder.razorpay_order_id,
        name: "DEBSUN Technologies",
        description: `Order ${payOrder.order_no}`,
        prefill: payOrder.prefill,
        theme: { color: "#D4AF37" },
        handler: async (response) => {
          try {
            const { data: verified } = await axios.post(`${API}/payment/verify`, {
              order_id: order.id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setConfirmedOrder(verified);
            await refreshCart();
            toast.success("Payment successful. Order confirmed.");
          } catch (e) {
            toast.error("Payment verification failed. Please contact support.");
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setPaying(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  if (confirmedOrder) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center px-6" data-testid="checkout-success">
        <div className="max-w-lg w-full text-center p-10 clip-frame border border-border bg-background">
          <CheckCircle2 size={40} className="mx-auto text-[color:var(--teal)]" />
          <h1 className="display display-section text-3xl mt-6">Order confirmed.</h1>
          <p className="mt-3 text-steel-text">
            Reference <span className="font-mono-2 text-[color:var(--gold)]">{confirmedOrder.order_no}</span> — {money(confirmedOrder.amount)} paid via Razorpay (Test Mode).
          </p>
          <Link to="/shop" data-testid="checkout-continue-shopping-btn"
            className="btn-gold clip-frame-sm px-6 py-3 text-sm uppercase tracking-widest font-mono-2 inline-flex items-center gap-2 mt-8">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  if (!cart?.items?.length) {
    return (
      <div className="min-h-screen pt-32 pb-24 text-center px-6" data-testid="checkout-empty-state">
        <div className="display display-section text-3xl">Your cart is empty.</div>
        <Link to="/shop" className="text-[color:var(--gold)] underline text-sm mt-4 inline-block">Browse the shop</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3">§ Checkout</div>
        <h1 className="display display-section text-4xl md:text-5xl tracking-[-0.03em]">Shipping &amp; payment.</h1>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-3">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">§ Shipping address</div>
            <Input placeholder="Full name" value={address.full_name} onChange={set("full_name")} data-testid="checkout-full-name" className="rounded-sm h-11" />
            <Input placeholder="Phone" value={address.phone} onChange={set("phone")} data-testid="checkout-phone" className="rounded-sm h-11" />
            <Input placeholder="Address line 1" value={address.address_line1} onChange={set("address_line1")} data-testid="checkout-address-1" className="rounded-sm h-11" />
            <Input placeholder="Address line 2 (optional)" value={address.address_line2} onChange={set("address_line2")} data-testid="checkout-address-2" className="rounded-sm h-11" />
            <div className="grid grid-cols-3 gap-3">
              <Input placeholder="City" value={address.city} onChange={set("city")} data-testid="checkout-city" className="rounded-sm h-11" />
              <Input placeholder="State" value={address.state} onChange={set("state")} data-testid="checkout-state" className="rounded-sm h-11" />
              <Input placeholder="Pincode" value={address.pincode} onChange={set("pincode")} data-testid="checkout-pincode" className="rounded-sm h-11" />
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="p-6 clip-frame border border-border bg-secondary/30">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">§ Order summary</div>
              <div className="space-y-3">
                {cart.items.map((it) => (
                  <div key={it.product_id} className="flex items-center justify-between text-sm" data-testid={`checkout-summary-item-${it.product_id}`}>
                    <span className="text-steel-text">{it.name} × {it.quantity}</span>
                    <span className="font-mono-2">{money(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="hairline my-4" />
              <div className="flex items-center justify-between">
                <span className="font-mono-2 text-xs uppercase tracking-widest text-muted-foreground">Total</span>
                <span className="display text-2xl" data-testid="checkout-total">{money(cart.subtotal)}</span>
              </div>
              <Button
                onClick={handlePay}
                disabled={paying || !requiredFilled}
                className="btn-gold rounded-none clip-frame-sm w-full mt-6 h-12 uppercase text-xs tracking-widest font-mono-2"
                data-testid="checkout-pay-btn"
              >
                {paying ? "Processing…" : `Pay ${money(cart.subtotal)} with Razorpay`}
              </Button>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
                <ShieldCheck size={13} /> Secured by Razorpay · Test Mode
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground" data-testid="checkout-support-note">
                Need help? <a href="mailto:support@debsuntechnologies.in" className="aurora-underline text-foreground">support@debsuntechnologies.in</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
